import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveTKey } from '../../../../i18n/resolve-key';
import { Box, IconButton, TextField } from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
import type { FilterControlProps, FilterOperator } from '../filters/filter-registry.js';
import {
  FILTER_OPERATORS,
  getOperatorsForType,
  registerFilterControl,
} from '../filters/filter-registry.js';
import { FilterOperatorMenu } from './FilterOperatorMenu.tsx';

type NumberFilterProps = FilterControlProps;

function useDebouncedCallback(callback: (val: string, endVal?: string) => void, delay: number) {
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);
  return React.useCallback(
    (val: string, endVal?: string) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => callback(val, endVal), delay);
    },
    [callback, delay]
  );
}

function normalizeNumericValue(columnType: string | undefined, raw: string): string | number {
  if (columnType === 'number') return Number(raw);
  // Finance decimal/quantity/money filters retain exact decimal strings and do
  // not round-trip through JavaScript Number.
  return raw;
}

export function NumberColumnFilter({ column, value, onChange, disabled }: NumberFilterProps) {
  const { t } = useTranslation();
  const accessibleLabel = t(resolveTKey(column.labelKey));
  const rangeEndLabel = `${accessibleLabel} ${t(resolveTKey('filters.to'))}`;
  const fieldType = column.fieldType ?? 'number';
  const allowedOperators = useMemo(() => {
    const candidates = getOperatorsForType(fieldType as never);
    const allowed = column.allowedFilterOperators;
    return allowed ? candidates.filter(operator => allowed.includes(operator.id)) : candidates;
  }, [column.allowedFilterOperators, fieldType]);
  const defaultOperator = allowedOperators.find(operator => operator.id === 'equals')?.id
    ?? allowedOperators[0]?.id
    ?? 'equals';
  const [localValue, setLocalValue] = useState<string>('');
  const [localEndValue, setLocalEndValue] = useState<string>('');
  const [localOperator, setLocalOperator] = useState<FilterOperator>(value?.operator ?? defaultOperator);

  useEffect(() => {
    if (value) {
      if (value.operator === 'between' && Array.isArray(value.value) && value.value.length === 2) {
        setLocalValue(String(value.value[0] ?? ''));
        setLocalEndValue(String(value.value[1] ?? ''));
      } else {
        setLocalValue(value.value == null ? '' : String(value.value));
        setLocalEndValue('');
      }
      setLocalOperator(value.operator);
    } else {
      setLocalValue('');
      setLocalEndValue('');
      setLocalOperator(defaultOperator);
    }
  }, [defaultOperator, value]);

  const operatorDescriptor =
    FILTER_OPERATORS.find(operator => operator.id === localOperator) ?? allowedOperators[0];
  const isRange = localOperator === 'between';
  const requiresValue = operatorDescriptor?.requiresValue !== false;

  const emit = React.useCallback((val: string, endVal?: string, operator = localOperator) => {
    const descriptor = FILTER_OPERATORS.find(candidate => candidate.id === operator);
    if (descriptor?.requiresValue === false) {
      onChange({ operator, value: null });
      return;
    }
    if (!val) {
      onChange(null);
      return;
    }
    if (operator === 'between') {
      if (!endVal) return;
      onChange({
        operator,
        value: [
          normalizeNumericValue(column.fieldType, val),
          normalizeNumericValue(column.fieldType, endVal),
        ],
      });
      return;
    }
    onChange({ operator, value: normalizeNumericValue(column.fieldType, val) });
  }, [column.fieldType, localOperator, onChange]);

  const debouncedChange = useDebouncedCallback(emit, 300);

  const handleOperatorChange = (operator: string) => {
    const nextOperator = operator as FilterOperator;
    setLocalOperator(nextOperator);
    if (nextOperator !== 'between') setLocalEndValue('');
    emit(localValue, localEndValue, nextOperator);
  };

  const handleClear = () => {
    setLocalValue('');
    setLocalEndValue('');
    onChange(null);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0 }}>
      {allowedOperators.length > 1 && (
        <FilterOperatorMenu
          column={column}
          currentOperator={localOperator}
          onChange={handleOperatorChange}
          allowedOperators={allowedOperators}
          disabled={disabled}
        />
      )}
      {requiresValue && (
        <Box sx={{ display: 'flex', gap: 0.5, flex: 1, minWidth: 0 }}>
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            type={fieldType === 'number' ? 'number' : 'text'}
            placeholder={accessibleLabel}
            value={localValue}
            onChange={event => {
              setLocalValue(event.target.value);
              debouncedChange(event.target.value, localEndValue);
            }}
            disabled={disabled}
            sx={{ minWidth: 0 }}
            slotProps={{
              htmlInput: {
                'aria-label': accessibleLabel,
                inputMode: 'decimal',
              },
            }}
          />
          {isRange && (
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              type={fieldType === 'number' ? 'number' : 'text'}
              placeholder={t(resolveTKey('filters.to'))}
              value={localEndValue}
              onChange={event => {
                setLocalEndValue(event.target.value);
                debouncedChange(localValue, event.target.value);
              }}
              disabled={disabled}
              sx={{ minWidth: 0 }}
              slotProps={{
                htmlInput: {
                  'aria-label': rangeEndLabel,
                  inputMode: 'decimal',
                },
              }}
            />
          )}
        </Box>
      )}
      {(localValue || localEndValue || !requiresValue) && (
        <IconButton
          size="small"
          onClick={handleClear}
          disabled={disabled}
          aria-label={t('common:clear')}
          sx={{ flexShrink: 0 }}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}

function createNumberFilter() {
  return React.memo(NumberColumnFilter);
}

registerFilterControl('number', () => createNumberFilter());
registerFilterControl('decimal', () => createNumberFilter());
registerFilterControl('quantity', () => createNumberFilter());
registerFilterControl('money', () => createNumberFilter());
